import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { ITierType, IPlanPricing } from "@util/pricing.util";
import { ContextType } from "react";

export type ITierContent = {
  type: ITierType;
  title: string;
  planPricing: IPlanPricing;
  costUnit: string;
  featureHeader: string;
  scheduleAssists: string;
  features: string[];
};

type IFeatureAvailability = "Enabled" | "Disabled" | "Limited" | "Unlimited" | "Other";

export type ITierFeatureMap = {
  [tier: string]: { enabled: IFeatureAvailability; description?: string };
};

export type IFeatureComparison = {
  feature: string;
  tierFeatureMap: ITierFeatureMap;
  tooltip?: string;
  isSectionHeader?: boolean;
};

export const freeTier: ITierContent = {
  type: "FreemiumOrg",
  title: "Free",
  planPricing: {
    monthlyCost: 0,
    yearlyCost: 0,
    contactSalesForPricing: false,
  },
  costUnit: "per user per month",
  featureHeader: "Access to a smart calendar assistant:",
  scheduleAssists: "schedule assists every two weeks",
  features: [
    "Limited Smart Holds for Focus Time and lunch",
    "Limited Autopilot usage",
    "Limited Scheduler usage",
    "Slack sync",
    "Personal-to-work calendar sync",
  ],
};

export const proOrgTier: ITierContent = {
  type: "ProOrg",
  title: "Pro",
  planPricing: {
    monthlyCost: 7.75,
    yearlyCost: 6.75,
    contactSalesForPricing: false,
  },
  costUnit: "per user per month billed annually",
  featureHeader: "Everything in Free, plus:",
  scheduleAssists: "Unlimited schedule assists ",
  features: [
    "Unlimited Smart Holds for Focus Time and lunch",
    "Unlimited Autopilot usage",
    "Unlimited Scheduler usage",
    "Custom Smart Holds for any event",
    "Automatic team availability calendar",
    "Automatic team no-meeting days",
    "Team calendar analytics",
    "Priority Support",
  ],
};

export const businessTier: ITierContent = {
  type: "BusinessOrg",
  title: "Business",
  planPricing: {
    monthlyCost: 11.50, // prettier-ignore
    yearlyCost: 11.50, // prettier-ignore
    contactSalesForPricing: false,
  },
  costUnit: "per user per month, billed annually",
  featureHeader: "Everything in Pro, plus:",
  scheduleAssists: "",
  features: [
    "Personalized onboarding and training",
    "Dedicated Customer Success Manager",
    "Custom contract & invoicing",
  ],
};

export const enterpriseTier: ITierContent = {
  type: "Enterprise",
  title: "Enterprise",
  planPricing: {
    monthlyCost: 0,
    yearlyCost: 0,
    contactSalesForPricing: false,
  },
  costUnit: "Contact our team to discuss how you can scale company-wide.",
  featureHeader: "Everything in Business, plus:",
  scheduleAssists: "schedule assists every two weeks",
  features: [
    "Limited Smart Holds for Focus Time and lunch",
    "Limited Autopilot usage",
    "Limited Scheduler usage",
    "Slack sync",
    "Personal-to-work calendar sync",
  ],
};

export const teamTiers: ITierContent[] = [freeTier, proOrgTier, businessTier];

export const getFeatureComparisons = ({
  t,
}: {
  t: ContextType<typeof TranslationContext>;
}): IFeatureComparison[] => [
  // {
  //   feature: 'Total seats',
  //   tierFeatureMap: {
  //     free: { enabled: true, description: 'Up to 50' },
  //     pro: { enabled: true, description: 'Up to 50' },
  //     business: { enabled: true, description: 'Unlimited' },
  //     enterprise: { enabled: true, description: 'Unlimited' },
  //   },
  // },
  {
    feature: t("pricing:feature-comparison-grid-autopilot") as string,
    tierFeatureMap: {
      free: { enabled: "Limited", description: "" },
      pro: { enabled: "Unlimited", description: "" },
      business: { enabled: "Unlimited", description: "" },
      enterprise: { enabled: "Unlimited", description: "" },
    },
    tooltip: t("pricing:feature-comparison-grid-autopilot-tooltip") as string,
  },
  {
    feature: "Focus Time holds",
    tierFeatureMap: {
      free: { enabled: "Limited", description: "" },
      pro: { enabled: "Unlimited", description: "" },
      business: { enabled: "Unlimited", description: "" },
      enterprise: { enabled: "Unlimited", description: "" },
    },
    tooltip:
      "A Smart Hold that prevents others from scheduling over your Focus Time by blocking it on your work calendar. Focus Time holds will automatically update as your schedule changes.",
  },
  {
    feature: "Lunch holds",
    tierFeatureMap: {
      free: { enabled: "Limited", description: "" },
      pro: { enabled: "Unlimited", description: "" },
      business: { enabled: "Unlimited", description: "" },
      enterprise: { enabled: "Unlimited", description: "" },
    },
    tooltip:
      "A Smart Hold that automatically protects your lunch every day, based on when you want to take it and how long you like it to be.",
  },
  {
    feature: "Travel time holds",
    tierFeatureMap: {
      free: { enabled: "Limited", description: "" },
      pro: { enabled: "Unlimited", description: "" },
      business: { enabled: "Unlimited", description: "" },
      enterprise: { enabled: "Unlimited", description: "" },
    },
    tooltip:
      "A Smart Hold that automatically factors in transit time on your schedule, for meetings with a location in the invite.",
  },
  {
    feature: "Smart scheduling suggestions",
    tierFeatureMap: {
      free: { enabled: "Limited", description: "" },
      pro: { enabled: "Unlimited", description: "" },
      business: { enabled: "Unlimited", description: "" },
      enterprise: { enabled: "Unlimited", description: "" },
    },
    tooltip:
      "Clockwise’s native scheduling experience that suggests the best time for new meetings by surfacing the time that costs the attendees the least amount of Focus Time.",
  },
  {
    feature: "Personal-to-work calendar sync",
    tierFeatureMap: {
      free: { enabled: "Limited", description: "" },
      pro: { enabled: "Unlimited", description: "" },
      business: { enabled: "Unlimited", description: "" },
      enterprise: { enabled: "Unlimited", description: "" },
    },
    tooltip:
      'Automatically syncs events from your personal Google Calendar account to your work calendar. Coworkers will see a "Busy (via Clockwise)" event.',
  },
  // {
  //   feature: "Automatic meeting color-coding",
  //   tierFeatureMap: {
  //     free: { enabled: true, description: "" },
  //     pro: { enabled: true, description: "" },
  //     business: { enabled: true, description: "" },
  //     enterprise: { enabled: true, description: "" },
  //   },
  //   tooltip:
  //     "Organize your calendar quickly with automatic meeting color-coding, customized to your preferences.",
  // },

  {
    feature: "🔗 Clockwise Links",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    isSectionHeader: true,
  },
  {
    feature: "Best meeting times highlighted",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `Clockwise will find the best time(s) to meet, based on your Focus Time needs and scheduling preferences. The best meeting times will be highlighted on the availability page for your link.`,
  },
  {
    feature: "Unlimited custom links",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `Create and send as many Clockwise Links as you'd like, for every type of external meeting that you schedule.`,
  },
  {
    feature: "Links dashboard",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `View and manage all your Clockwise Links in one place.`,
  },
  {
    feature: "Google Meet and Zoom integrations",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `A Google Meet or Zoom link can be automatically added to any meeting scheduled with Clockwise Links.`,
  },
  {
    feature: "Automatic email notifications",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `Clockwise will automatically notify you and your attendee when a meeting scheduled with your link is confirmed, canceled, or rescheduled.`,
  },

  {
    feature: "🚀 Team Productivity Tools",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    isSectionHeader: true,
  },
  {
    feature: "Automatic no-meeting day",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `Automatically protects your team’s designated no-meeting day, and notifies meeting organizers when they try to schedule over your no-meeting day.`,
  },
  {
    feature: "Automatic out-of-office calendar",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `Automatically syncs out of office events to a shared team calendar, keeping everyone on the same page regarding availability.`,
  },
  {
    feature: "At-a-glance team analytics",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `A weekly summary of statistics and visualization on team members' time allocation, working preference, and top collaborators.`,
  },

  {
    feature: "🧠 Advanced calendar controls",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    isSectionHeader: true,
  },
  {
    feature: t("pricing:feature-comparison-grid-custom-autopilot-settings") as string,
    tierFeatureMap: {
      free: { enabled: "Disabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    // TODO: (nikita) update with flexible meeting copy
    tooltip:
      "Flexibly control the time of day that meetings are scheduled at or rescheduled to, based on attendee preferences.",
  },
  {
    feature: "Smart meeting breaks",
    tierFeatureMap: {
      free: { enabled: "Disabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip:
      "Automatically build meeting breaks into your day, based on your personal preferences.",
  },

  {
    feature: "🔁 Integrations",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    isSectionHeader: true,
  },
  {
    feature: "Slack",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `Automatically syncs your Slack status so your team can see if you're in a Focus Time block or out of office.`,
  },
  {
    feature: "Zoom",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: "Sync your account to add Zoom Meetings to your invites with one click.",
  },
  {
    feature: "Asana",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: "Schedule Asana tasks on your calendar and give your productivity a jolt.",
  },

  {
    feature: "🔐 Security and support",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    isSectionHeader: true,
  },
  // {
  //   feature: "SOC 2 Type 1",
  //   tierFeatureMap: {
  //     free: { enabled: "Enabled", description: "" },
  //     pro: { enabled: "Enabled", description: "" },
  //     business: { enabled: "Enabled", description: "" },
  //     enterprise: { enabled: "Enabled", description: "" },
  //   },
  // },
  {
    feature: "Google SSO",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: "Sign in securely to Clockwise with your GSuite work account.",
  },
  {
    feature: "User management",
    tierFeatureMap: {
      free: { enabled: "Disabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `Ability to view and edit paid users in the Members and Billing portal.`,
  },
  {
    feature: "Admin controls & user roles",
    tierFeatureMap: {
      free: { enabled: "Disabled", description: "" },
      pro: { enabled: "Disabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `Ability to assign different user roles and allow admins to control who can invite new users to join the subscription.`,
  },
  {
    feature: "Divisional plans",
    tierFeatureMap: {
      free: { enabled: "Disabled", description: "" },
      pro: { enabled: "Disabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `Ability for buyers to buy for a Division. A Division is defined as a custom group of users that is between a small team of less than 50 users and a full org.`,
  },
  {
    feature: "Custom contract and invoicing",
    tierFeatureMap: {
      free: { enabled: "Disabled", description: "" },
      pro: { enabled: "Disabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: "Customize your contract or invoice, tailored to your organization’s needs.",
  },
  {
    feature: "Advanced privacy options",
    tierFeatureMap: {
      free: { enabled: "Disabled", description: "" },
      pro: { enabled: "Disabled", description: "" },
      business: { enabled: "Disabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `Option to not store meeting descriptions in the Clockwise database.`,
  },
  {
    feature: "User provisioning (SCIM)",
    tierFeatureMap: {
      free: { enabled: "Disabled", description: "" },
      pro: { enabled: "Disabled", description: "" },
      business: { enabled: "Disabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: `Allows admins to securely manage paid users in Clockwise by connecting directly to your  corporate identity provider.`,
  },

  {
    feature: "💁 Customer service",
    tierFeatureMap: {
      free: { enabled: "Enabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    isSectionHeader: true,
  },
  {
    feature: "Priority Support",
    tierFeatureMap: {
      free: { enabled: "Disabled", description: "" },
      pro: { enabled: "Enabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: "Get quick answers to your Customer Support questions.",
  },
  // {
  //   feature: "Personalized onboarding and training",
  //   tierFeatureMap: {
  //     free: { enabled: "Disabled", description: "" },
  //     pro: { enabled: "Other", description: "3 months" },
  //     business: { enabled: "Enabled", description: "" },
  //     enterprise: { enabled: "Enabled", description: "" },
  //   },
  //   tooltip:
  //     "Receive onboarding and training sessions tailored to your team’s needs, at no additional cost.",
  // },
  {
    feature: "Dedicated Customer Success Manager",
    tierFeatureMap: {
      free: { enabled: "Disabled", description: "" },
      pro: { enabled: "Enabled", description: "3 months" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: "Receive best-in-class service from a dedicated Customer Success expert.",
  },
  {
    feature: "Meeting Culture Workshop",
    tierFeatureMap: {
      free: { enabled: "Disabled", description: "" },
      pro: { enabled: "Disabled", description: "" },
      business: { enabled: "Enabled", description: "" },
      enterprise: { enabled: "Enabled", description: "" },
    },
    tooltip: "Receive best-in-class service from a dedicated Customer Success expert.",
  },
];

export const pricingFaqs = [
  {
    question: `How do I pay for Clockwise’s subscription?`,
    answer: `Clockwise offers both monthly and annual subscriptions. When you are ready to make a selection, we’ll walk you through step-by-step instructions on how to process your payment, in the product. Should you have any questions about larger plans or custom terms, email sales@getclockwise.com.`,
  },
  {
    question: `I have another question about Clockwise’s plans`,
    answer: `Please reach out to our team at sales@getclockwise.com, and we’ll do our best to address your question.`,
  },
];
